import { type FC } from 'react';
import { IconBadge } from '@components/IconBadge';
import { Show } from '@components/Show';
import { isBlockValid } from '@ContractBuilder/utils/block-validation';
import { BlockError } from '@domain/schemas/block-error';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { useDeepCompareMemo } from '@hooks/use-deep-compare-memo';
import { BLOCK_ACTIONS_Z_INDEX } from '@root/src/constants/z-indices';
import clsx from 'clsx';

import Tooltip from '../../../../../components/Tooltip';

const messages = {
  [BlockError.BlockEmpty]: 'Block is empty',
  [BlockError.DatapointEmpty]: 'Block has incomplete datapoints',
  [BlockError.VariationNotSelected]: 'Select a variation of this block',
  [BlockError.UnresolvedComments]: 'Block has unresolved comments',
};

interface BlockAlertProps {
  block: ResourceBlock;
  hasBeenMovedByRenewal: boolean;
}

const wrapperClasses = ['hover:cursor-pointer', 'transition-all', 'duration-200', 'ease-in-out', 'text-xs'];

export const BlockAlert: FC<BlockAlertProps> = ({ block, hasBeenMovedByRenewal }) => {
  const isValid = useDeepCompareMemo(() => isBlockValid(block), [block]);

  if (isValid && !hasBeenMovedByRenewal) {
    return null;
  }

  return (
    <div
      className={clsx('absolute top-1 left-0 flex -translate-x-1/2 transform flex-col gap-1', BLOCK_ACTIONS_Z_INDEX)}
    >
      <Show when={!isValid}>
        <Tooltip
          content={
            <ul className="text-center">
              {block.validationErrors?.map((problem) => (
                <li key={problem}>{messages[problem]}</li>
              ))}
            </ul>
          }
        >
          <IconBadge className={clsx(wrapperClasses)} data-testid="block-alert" name="alert-circle" />
        </Tooltip>
      </Show>
      <Show when={hasBeenMovedByRenewal}>
        <Tooltip content="Block moved during renewal, please check position">
          <IconBadge className={clsx(wrapperClasses, 'flex items-center justify-center')} name="move" />
        </Tooltip>
      </Show>
    </div>
  );
};
